/* eslint-disable complexity */
/* eslint-disable no-case-declarations */
import isEqual from 'lodash/isEqual';

import { checkIfPassesFilters, getSortedData } from './filter-table-helper';
import {
  FilterTableAction,
  FilterTableActionTypes,
  FILTER_TYPE,
  TableFiltersState,
  TABLE_COLUMN
} from './filter-table-types';

export const filtersTableReducer = (state: TableFiltersState, action: FilterTableAction): TableFiltersState => {
  switch (action.type) {
    case FilterTableActionTypes.TOGGLE_FILTER_MENU:
      return { ...state, ...action.payload };
    case FilterTableActionTypes.SET_INITIAL_DATA:
      return {
        ...state,
        data: [...action.payload.data],
        displayedData: action.payload.data.filter(d => checkIfPassesFilters(d, state.filters))
      };
    case FilterTableActionTypes.SET_FILTERS_AND_SORT:
      const { filters: newFilters, sort, sortByColumn, isUsingLocalStorage } = action.payload;

      // if something not existing is saved in local storage, reset to initial state
      if (isUsingLocalStorage) {
        if (
          (sortByColumn && !state.filters[sortByColumn]) ||
          !isEqual(Object.keys(newFilters), Object.keys(state.filters))
        ) {
          return { ...state };
        }
      }

      const newData = state?.data?.filter(d => checkIfPassesFilters(d, newFilters));

      return {
        ...state,
        displayedData:
          sort && sortByColumn && newData
            ? getSortedData(newData, sortByColumn, sort, state.filters[sortByColumn].filterType)
            : newData,
        filters: { ...newFilters },
        sort,
        sortByColumn
      };
    case FilterTableActionTypes.CLEAR_ACTIVE_COLUMN_FILTER:
      if (state.activeColumn && state.data) {
        const initialFiltersActiveColumn = action.payload.initialFilters[state.activeColumn];
        const newFilters = {
          ...state.filters,
          [state.activeColumn]: {
            ...initialFiltersActiveColumn
          }
        };
        const newSortByColumn = state.activeColumn === state.sortByColumn ? undefined : state.sortByColumn;
        const newSort = state.activeColumn === state.sortByColumn ? undefined : state.sort;
        const newData = state.data.filter(d => checkIfPassesFilters(d, newFilters));
        const newSortedData =
          newSortByColumn && newSort
            ? getSortedData(newData, newSortByColumn, newSort, state.filters[newSortByColumn].filterType)
            : newData;
        return {
          ...state,
          sortByColumn: newSortByColumn,
          displayedData: [...newSortedData],
          sort: newSort,
          filters: newFilters,
          filterAnchorElement: null,
          filterUpdatedCount: (state.filterUpdatedCount || 0) + 1,
          sortUpdatedCount: (state.sortUpdatedCount || 0) + 1
        };
      }
    case FilterTableActionTypes.CLEAR_FILTERS:
      return { ...state, filters: { ...action.payload.initialFilters } };
    case FilterTableActionTypes.SET_SORT:
      if (state.displayedData && state.activeColumn) {
        const newSortedData = getSortedData(
          state.displayedData,
          state.activeColumn,
          action.payload.sort,
          state.filters[state.activeColumn].filterType
        );
        // set wanted sort to value, all others sort to undefined !
        return {
          ...state,
          displayedData: [...newSortedData],
          filterAnchorElement: null,
          sortByColumn: state.activeColumn,
          sort: action.payload.sort,
          sortUpdatedCount: (state.sortUpdatedCount || 0) + 1
        };
      }
      return state;
    case FilterTableActionTypes.SUBMIT_EXPECTED_DATE_FILTERS:
      const { fromDate: fromExpectedDate, toDate: toExpectedDate, label } = action.payload;
      if (state.displayedData && state.data) {
        const newExpectedDateFilters = {
          ...state.filters,
          [TABLE_COLUMN.EXPECTED_DATE]: {
            ...state.filters[TABLE_COLUMN.EXPECTED_DATE],
            fromDate: fromExpectedDate,
            toDate: toExpectedDate,
            label
          }
        };

        const newSubmitExpectedDateData = state.data.filter(d => checkIfPassesFilters(d, newExpectedDateFilters));

        return {
          ...state,
          displayedData:
            state.sortByColumn && state.sort
              ? getSortedData(
                  newSubmitExpectedDateData,
                  state.sortByColumn,
                  state.sort,
                  newExpectedDateFilters[state.sortByColumn].filterType
                )
              : newSubmitExpectedDateData,
          filters: newExpectedDateFilters
        };
      }
      return state;
    case FilterTableActionTypes.SUBMIT_FILTERS:
      const {
        checkedFilteredData,
        searchQuery,
        fromValue,
        toValue,
        fromDate,
        toDate,
        fromTime,
        toTime,
        currentDataSelectOptions,
        selectedValues
      } = action.payload;
      if (state.displayedData && state.activeColumn && state.data) {
        if (state.filters[state.activeColumn].filterType === FILTER_TYPE.SELECT && selectedValues) {
          const newFilters = {
            ...state.filters,
            [state.activeColumn]: {
              ...state.filters[state.activeColumn],
              selectedValues
            }
          };

          const newData = state.data.filter(d => checkIfPassesFilters(d, newFilters));
          return {
            ...state,
            displayedData:
              state.sortByColumn && state.sort
                ? getSortedData(newData, state.sortByColumn, state.sort, newFilters[state.sortByColumn].filterType)
                : newData,
            filters: newFilters,
            filterUpdatedCount: (state.filterUpdatedCount || 0) + 1
          };
        }
        if (state.filters[state.activeColumn].filterType === FILTER_TYPE.NUMBER && (fromValue || toValue)) {
          const newFilters = {
            ...state.filters,
            [state.activeColumn]: {
              ...state.filters[state.activeColumn],
              fromValue,
              toValue
            }
          };

          const newData = state.data.filter(d => checkIfPassesFilters(d, newFilters));

          return {
            ...state,
            displayedData:
              state.sortByColumn && state.sort
                ? getSortedData(newData, state.sortByColumn, state.sort, newFilters[state.sortByColumn].filterType)
                : newData,
            filters: newFilters,
            filterUpdatedCount: (state.filterUpdatedCount || 0) + 1
          };
        }
        if (state.filters[state.activeColumn].filterType === FILTER_TYPE.DATE && (fromDate || toDate)) {
          const newFilters = {
            ...state.filters,
            [state.activeColumn]: {
              ...state.filters[state.activeColumn],
              fromDate,
              toDate
            }
          };

          const newData = state.data.filter(d => checkIfPassesFilters(d, newFilters));

          return {
            ...state,
            displayedData:
              state.sortByColumn && state.sort
                ? getSortedData(newData, state.sortByColumn, state.sort, newFilters[state.sortByColumn].filterType)
                : newData,
            filters: newFilters,
            filterUpdatedCount: (state.filterUpdatedCount || 0) + 1
          };
        }
        if (state.filters[state.activeColumn].filterType === FILTER_TYPE.TIME && (fromTime || toTime)) {
          const newFilters = {
            ...state.filters,
            [state.activeColumn]: {
              ...state.filters[state.activeColumn],
              fromTime,
              toTime
            }
          };

          const newData = state.data.filter(d => checkIfPassesFilters(d, newFilters));

          return {
            ...state,
            displayedData:
              state.sortByColumn && state.sort
                ? getSortedData(newData, state.sortByColumn, state.sort, newFilters[state.sortByColumn].filterType)
                : newData,
            filters: newFilters,
            filterUpdatedCount: (state.filterUpdatedCount || 0) + 1
          };
        }
        if (state.filters[state.activeColumn].filterType === FILTER_TYPE.TEXT && checkedFilteredData) {
          const newFilters = {
            ...state.filters,
            [state.activeColumn]: {
              ...state.filters[state.activeColumn],
              searchQuery,
              checkedFilteredData: { ...checkedFilteredData }
            }
          };

          const selectedIds = Object.keys(checkedFilteredData).filter(id => checkedFilteredData[id]);
          const newData =
            currentDataSelectOptions && selectedIds
              ? currentDataSelectOptions.filter(d => selectedIds.indexOf(d.id) !== -1)
              : state.displayedData.filter(d => selectedIds.indexOf(d.id) !== -1);

          return {
            ...state,
            displayedData:
              state.sortByColumn && state.sort
                ? getSortedData(newData, state.sortByColumn, state.sort, newFilters[state.sortByColumn].filterType)
                : newData,
            filters: newFilters,
            filterUpdatedCount: (state.filterUpdatedCount || 0) + 1
          };
        }
      }
    default:
      return state;
  }
};
